// src/lib/salesCriteria.ts
//
// Read/write the Sales dashboard criteria to/from the URL
// (`?brand=Chief&year_from=2025&year_to=2025`) and to/from
// `localStorage` (so the criteria survive a hard refresh even
// when the user navigated here without query params).
//
// This is the source of truth for the Sales page filters, and
// it's the same shape the Teddy 'sales' domain tool emits.

export type GroupBy = "brand" | "category" | "product";

export interface SalesCriteriaState {
  year_from: number | null;
  year_to: number | null;
  month_from: number | null;  // 1-12
  month_to: number | null;
  brand_id: number | null;
  brand_name: string | null;
  customer_id: number | null;
  product_id: number | null;
  category: string | null;
  group_by: GroupBy;
}

export const DEFAULT_CRITERIA: SalesCriteriaState = {
  year_from: null, year_to: null,
  month_from: null, month_to: null,
  brand_id: null, brand_name: null,
  customer_id: null, product_id: null, category: null,
  group_by: "brand",
};

const LS_KEY = "sales.criteria.v1";

function fromSearchParams(s: URLSearchParams): SalesCriteriaState {
  const numOrNull = (k: string): number | null => {
    const v = s.get(k);
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    year_from: numOrNull("year_from"),
    year_to: numOrNull("year_to"),
    month_from: numOrNull("month_from"),
    month_to: numOrNull("month_to"),
    brand_id: numOrNull("brand_id"),
    brand_name: s.get("brand") || null,
    customer_id: numOrNull("customer_id"),
    product_id: numOrNull("product_id"),
    category: s.get("category") || null,
    group_by: (s.get("group_by") as GroupBy) || "brand",
  };
}

function toSearchParams(c: SalesCriteriaState): URLSearchParams {
  const out = new URLSearchParams();
  const set = (k: string, v: string | number | null) => {
    if (v !== null && v !== "" && v !== undefined) out.set(k, String(v));
  };
  set("year_from", c.year_from);
  set("year_to", c.year_to);
  set("month_from", c.month_from);
  set("month_to", c.month_to);
  set("brand_id", c.brand_id);
  set("brand", c.brand_name);
  set("customer_id", c.customer_id);
  set("product_id", c.product_id);
  set("category", c.category);
  set("group_by", c.group_by);
  return out;
}

export function readCriteriaFromURL(): SalesCriteriaState {
  if (typeof window === "undefined") return DEFAULT_CRITERIA;
  const s = new URLSearchParams(window.location.search);
  const fromUrl = fromSearchParams(s);
  if (Array.from(s.keys()).length > 0) {
    // URL wins over localStorage; persist for next visit
    try { localStorage.setItem(LS_KEY, JSON.stringify(fromUrl)); } catch {}
    return fromUrl;
  }
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULT_CRITERIA, ...JSON.parse(raw) };
  } catch {}
  return DEFAULT_CRITERIA;
}

export function writeCriteriaToURL(c: SalesCriteriaState): void {
  if (typeof window === "undefined") return;
  const params = toSearchParams(c);
  const url = new URL(window.location.href);
  params.forEach((v, k) => url.searchParams.set(k, v));
  // Drop empty params so the URL stays clean
  Array.from(url.searchParams.keys()).forEach((k) => {
    if (!params.has(k)) url.searchParams.delete(k);
  });
  window.history.replaceState(null, "", url.toString());
  try { localStorage.setItem(LS_KEY, JSON.stringify(c)); } catch {}
}

export function toApiPayload(c: SalesCriteriaState) {
  return {
    year_from: c.year_from, year_to: c.year_to,
    month_from: c.month_from, month_to: c.month_to,
    brand_id: c.brand_id, brand_name: c.brand_name,
    customer_id: c.customer_id, product_id: c.product_id,
    category: c.category, group_by: c.group_by,
  };
}

export function clearCriteria() {
  try { localStorage.removeItem(LS_KEY); } catch {}
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState(null, "", url.toString());
  }
}

export function summarizeCriteria(c: SalesCriteriaState): string {
  const parts: string[] = [];
  if (c.brand_name) parts.push(`brand: ${c.brand_name}`);
  else if (c.brand_id) parts.push(`brand #${c.brand_id}`);
  if (c.year_from && c.year_to && c.year_from === c.year_to)
    parts.push(`${c.year_from}`);
  else if (c.year_from || c.year_to)
    parts.push(`${c.year_from ?? "—"}–${c.year_to ?? "—"}`);
  if (c.month_from && c.month_to && c.month_from === c.month_to)
    parts.push(`month ${c.month_from}`);
  else if (c.month_from || c.month_to)
    parts.push(`months ${c.month_from ?? "—"}–${c.month_to ?? "—"}`);
  if (c.category) parts.push(`category "${c.category}"`);
  if (c.customer_id) parts.push(`customer #${c.customer_id}`);
  if (c.product_id) parts.push(`product #${c.product_id}`);
  return parts.join(" · ") || "all data";
}
